/**
 * AUI Surface
 *
 * Renders a complete surface spec by building a node map from the flat
 * updates list and rendering from the root node.
 */

import { useEffect, useMemo, useRef } from 'react';
import { X } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { formatRelativeTime } from '@/pages/activity/utils';
import { AuiNodeRenderer } from './AuiNodeRenderer';
import { AuiSurfaceContext } from './AuiSurfaceContext';
import { AuiFillPathProvider, computeFillPath } from './AuiFillPathContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { cn } from '@/lib/cn';
import { rewriteVfsUrisInNode } from '@/lib/vfs-url';
import { normalizeAuiNodeProps } from './schemas/normalize';
import { useCanvases } from '@/contexts/CanvasContext';
import { SurfaceActionsMenu } from '../canvas/SurfaceActionsMenu';
import type { Surface, AuiNode, AuiAction } from '../../types/canvases';
import { cleanDisplayText } from '@/utils/display-text';

const headingVariants = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

/** Depth-first search for the first Text heading node in the tree. */
function findFirstHeading(
  node: AuiNode,
  nodeMap: Map<string, AuiNode>,
  parentId?: string,
): { node: AuiNode; parentId: string } | null {
  const type = node.component.type;
  const props = node.component.props ?? {};
  const variant = (props.variant as string) ?? 'p';
  const text = (props.text as string) ?? (props.content as string) ?? '';

  if (type === 'Text' && (headingVariants.has(variant) || /^#{1,6}\s/.test(text))) {
    return parentId ? { node, parentId } : null;
  }

  for (const childId of node.component.children ?? []) {
    const child = nodeMap.get(childId);
    if (child) {
      const result = findFirstHeading(child, nodeMap, node.id);
      if (result) return result;
    }
  }
  return null;
}

/** Remove a child ID from its parent's children list; if parent becomes empty, recurse up. */
function removeNodeFromParent(
  nodeId: string,
  ancestorId: string,
  nodeMap: Map<string, AuiNode>,
) {
  // Find the parent that directly holds nodeId
  for (const [id, n] of nodeMap) {
    const children = n.component.children;
    if (children?.includes(nodeId)) {
      const filtered = children.filter((c) => c !== nodeId);
      nodeMap.set(id, {
        ...n,
        component: { ...n.component, children: filtered },
      });
      // If parent is now childless and isn't the ancestor root, prune it too
      if (filtered.length === 0 && id !== ancestorId) {
        removeNodeFromParent(id, ancestorId, nodeMap);
      }
      break;
    }
  }
}

interface AuiSurfaceProps {
  surface: Surface;
  onAction?: (action: AuiAction) => Promise<void>;
  onDismiss?: () => void;
  /** Optional drag handle rendered in the header (left of the actions menu). */
  dragHandle?: React.ReactNode;
}

const API_BASE = import.meta.env.VITE_API_URL || '';

export function AuiSurface({ surface, onAction, onDismiss, dragHandle }: AuiSurfaceProps) {
  const { conversationId, setSurfaceUiStateRaw } = useCanvases();
  const updates = useMemo(() => surface.spec?.updates ?? [], [surface.spec?.updates]);

  const nodeMap = useMemo(() => {
    const map = new Map<string, AuiNode>();
    for (const node of updates) {
      // Alias repair (e.g. MarkdownEditor `source` → `contentUrl`) for specs
      // persisted before write-time normalization existed. Must run BEFORE
      // the vfs:// rewrite so an aliased content URL still gets rewritten.
      const normalized = normalizeAuiNodeProps(node);
      const rewritten = conversationId
        ? rewriteVfsUrisInNode(normalized, conversationId, API_BASE)
        : normalized;
      map.set(node.id, rewritten);
    }
    return map;
  }, [updates, conversationId]);

  // Find root: prefer "root" id, otherwise find unreferenced nodes.
  // If multiple roots exist, synthesize a wrapper Column.
  const rootId = useMemo(() => {
    if (nodeMap.has('root')) return 'root';

    const referencedIds = new Set<string>();
    for (const node of updates) {
      for (const childId of node.component.children ?? []) {
        referencedIds.add(childId);
      }
    }
    const unreferenced = updates.filter((n) => !referencedIds.has(n.id));

    if (unreferenced.length <= 1) {
      return unreferenced[0]?.id ?? updates[0]?.id;
    }

    // Multiple roots — synthesize a wrapper Column
    const syntheticRoot: AuiNode = {
      id: '__synthetic_root__',
      component: {
        type: 'Column',
        props: { gap: 4 },
        children: unreferenced.map((n) => n.id),
      },
    };
    nodeMap.set(syntheticRoot.id, syntheticRoot);
    return syntheticRoot.id;
  }, [nodeMap, updates]);

  // Deduplicate: if the surface title matches the first heading in the content
  // tree, remove the content heading to avoid a double header.
  useMemo(() => {
    if (!surface.title || !rootId) return;
    const rootNode = nodeMap.get(rootId);
    if (!rootNode) return;

    const heading = findFirstHeading(rootNode, nodeMap);
    if (!heading) return;

    const normalizedTitle = cleanDisplayText(surface.title).toLowerCase();
    const headingText = cleanDisplayText(
      (heading.node.component.props?.text as string) ??
        (heading.node.component.props?.content as string) ??
        '',
    ).toLowerCase();

    if (headingText === normalizedTitle) {
      removeNodeFromParent(heading.node.id, rootId, nodeMap);
    }
  }, [surface.title, rootId, nodeMap]);

  // Stale-slice GC — drop uiState entries whose nodeId is no longer in the
  // current spec. Runs at most once per surfaceId mount so it can't loop on
  // its own dispatched updates.
  const gcRanRef = useRef<string | null>(null);
  useEffect(() => {
    if (gcRanRef.current === surface.surfaceId) return;
    gcRanRef.current = surface.surfaceId;
    if (!surface.uiState) return;
    const stale = Object.keys(surface.uiState).filter((id) => !nodeMap.has(id));
    if (stale.length === 0) return;
    const cleaned: Record<string, unknown> = { ...surface.uiState };
    for (const id of stale) delete cleaned[id];
    setSurfaceUiStateRaw(surface.surfaceId, cleaned);
  }, [surface.surfaceId, surface.uiState, nodeMap, setSurfaceUiStateRaw]);

  // These hooks must run on every render — keep them above the early return
  // below so the hook call order stays stable (Rules of Hooks).
  const surfaceCtx = useMemo(
    () => ({ runId: surface.runId, surfaceId: surface.surfaceId }),
    [surface.runId, surface.surfaceId],
  );

  const fillPath = useMemo(
    () => (rootId ? computeFillPath(rootId, nodeMap) : new Set<string>()),
    [rootId, nodeMap],
  );

  if (!rootId) return null;

  const showHeader = surface.title || onDismiss || dragHandle;
  // When the surface has a fill anchor the Card should claim its parent's
  // available height so the cascade reaches the leaf editor. SurfaceLayout
  // only provides bounded height in single-surface mode, so this is a no-op
  // in multi-surface stacks (h-full collapses to content size).
  const isFillSurface = fillPath.size > 0;

  return (
      <AuiSurfaceContext.Provider value={surfaceCtx}>
        <Card
          data-testid="aui-surface"
          data-surface-id={surface.surfaceId}
          className={cn(isFillSurface && 'flex flex-col h-full')}
        >
          {showHeader && (
            <CardHeader className="flex flex-row items-center justify-between">
              {surface.title ? (
                <CardTitle className="text-lg">{cleanDisplayText(surface.title)}</CardTitle>
              ) : (
                <div />
              )}
              <div className="group/surface-actions flex items-center gap-2">
                {(surface.updatedAt || surface.createdAt) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-muted-foreground cursor-default">
                        {surface.updatedAt && surface.updatedAt !== surface.createdAt
                          ? `updated ${formatRelativeTime(surface.updatedAt)}`
                          : formatRelativeTime(surface.createdAt!)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {new Date((surface.updatedAt && surface.updatedAt !== surface.createdAt ? surface.updatedAt : surface.createdAt)!).toLocaleString()}
                    </TooltipContent>
                  </Tooltip>
                )}
                <div className="flex items-center gap-1">
                  {dragHandle}
                  {onDismiss && (
                    <button
                      type="button"
                      onClick={onDismiss}
                      aria-label="Dismiss"
                      title="Dismiss"
                      className="rounded p-1 text-muted-foreground opacity-0 hover:bg-muted hover:text-foreground focus-ring group-hover/surface-actions:opacity-100"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <SurfaceActionsMenu surface={surface} onDismiss={onDismiss} />
                </div>
              </div>
            </CardHeader>
          )}
          <CardContent
            className={cn(
              showHeader && 'pt-0',
              isFillSurface && 'flex-1 min-h-0 flex flex-col',
            )}
          >
            <div
              className={cn(
                'flex flex-col gap-4',
                isFillSurface && 'flex-1 min-h-0',
              )}
            >
              <AuiFillPathProvider fillPath={fillPath}>
                <AuiNodeRenderer nodeId={rootId} nodeMap={nodeMap} onAction={onAction} />
              </AuiFillPathProvider>
            </div>
          </CardContent>
        </Card>
      </AuiSurfaceContext.Provider>
  );
}
